import { navigationLocationId } from '@/lib/tableViewNavigation.mjs'
import useNavigationViews from '@/hooks/useNavigationViews'
import useUserLocation from '@/hooks/useUserLocation'
import useMe from '@/hooks/useMe'
import { tableViewNavigationUrl } from '@/lib/tableViewNavigation.mjs'
import { useState, useEffect, useRef } from 'react'

import { motion, AnimatePresence } from 'motion/react'
import { Tooltip, Loader } from '@mantine/core'
import { Link, useNavigate } from 'react-router-dom'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
    ChevronLeft,
    ChevronRight,
    SearchLg,
    Scan,
    Hash02,
} from '@untitled-ui/icons-react'
import classNames from 'classnames'
import { flattenDeep } from 'lodash'
import { useBus } from 'react-bus'
import { useNavigationItems } from '@/hooks/useNavigationItems'

const NavItem = ({
    icon,
    label,
    badge,
    warning,
    href,
    hasSubitems,
    isActive,
    isMobile,
    onClick,
    onNavigate,
    loading,
    disabled,
}) => {
    const Icon = icon

    const content = (
        <div
            className={classNames(
                'flex items-center gap-3 px-4 rounded-xl transition-colors duration-150 select-none hover:bg-gray-100 active:bg-gray-200',
                'py-2.5 h-[38px]',
                {
                    'bg-gray-100 hover:bg-gray-200': isActive,
                }
            )}
        >
            {loading ? <Loader size={18} color="gray" /> : Icon && (
                <Icon
                    width={18}
                    height={18}
                    className={classNames('shrink-0', {
                        'text-gray-900': isActive,
                        'text-gray-400': !isActive,
                    })}
                />
            )}

            <span
                className={classNames('flex-1 text-sm', {
                    'text-gray-900 font-medium': isActive,
                    'text-gray-600': !isActive,
                })}
            >
                {label}
            </span>

            <div className="flex items-center gap-1.5">
                {badge > 0 && (
                    <div className="bg-baltic text-white rounded-full text-[11px] font-medium min-w-[20px] text-center px-1.5 py-0.5">
                        {badge > 999 ? '999+' : badge}
                    </div>
                )}

                {warning > 0 && (
                    <div className="bg-red-500 text-white rounded-full text-[11px] font-medium min-w-[20px] text-center px-1.5 py-0.5">
                        {warning > 999 ? '999+' : warning}
                    </div>
                )}

                {hasSubitems && (
                    <ChevronRight
                        width={16}
                        height={16}
                        className="text-gray-300"
                    />
                )}
            </div>
        </div>
    )

    if (hasSubitems) {
        return <button type="button" disabled={disabled} onClick={onClick} className="w-full cursor-pointer text-left disabled:cursor-wait">{content}</button>
    }

    return (
        <Link to={href ?? ''} onClick={() => onNavigate?.(href)}>
            {content}
        </Link>
    )
}

export default function BackofficeLayoutNavigation({ onNavigate }) {
    const bus = useBus()
    const allItems = useNavigationItems()
    const navigate = useNavigate()
    const { data: cachedViews, mutate: refreshNavigation } = useNavigationViews()
    const { userLocationId } = useUserLocation()
    const { isAdmin } = useMe()
    const [openingLabel, setOpeningLabel] = useState(null)
    const openingRequest = useRef(0)

    const [drilldownItem, setDrilldownItem] = useState(null)
    const [direction, setDirection] = useState(1) // 1 = forward, -1 = back
    const [optimisticHref, setOptimisticHref] = useState(null)

    const location = useLocation()
    const [searchParams] = useSearchParams()
    const pathname = location.pathname
    const currentDrilldown = allItems.find((item) => item.label === drilldownItem?.label) ?? drilldownItem

    // Clear optimistic state when pathname catches up
    useEffect(() => {
        setOptimisticHref(null)
    }, [pathname, location.search])

    useEffect(() => {
        setOpeningLabel(null)
        return () => { openingRequest.current += 1 }
    }, [location.key])

    const handleDrilldown = async (item) => {
        const request = ++openingRequest.current
        let views = cachedViews
        if (views === undefined) {
            setOpeningLabel(item.label)
            try { views = await refreshNavigation() } catch { /* Keep the fixed navigation usable when offline. */ }
            if (request !== openingRequest.current) return
            setOpeningLabel(null)
        } else {
            // Use cached views immediately, including an empty result.
            void refreshNavigation().catch(() => {})
        }
        setDirection(1)
        setDrilldownItem(item)
        if (!onNavigate && !isCurrentItemActive(item)) {
            const firstView = views?.find((view) => view.table_key === item.viewTableKey)
            const currentLocation = navigationLocationId(searchParams.get('location_id'), userLocationId, isAdmin)
            const href = firstView ? tableViewNavigationUrl(firstView, currentLocation)
                : item.viewTableKey && views ? `${item.tablePath}?location_id=${currentLocation}` : item.subitems?.[0]?.href
            if (href) {
                setOptimisticHref(href)
                navigate(href)
            }
        }
    }

    const handleBack = () => {
        setDirection(-1)
        setDrilldownItem(null)
    }

    const handleNavigate = (href) => {
        if (href) setOptimisticHref(href)
        onNavigate?.()
    }

    const getActiveSubitemIndex = (subitems = []) => {
        const viewIndex = subitems.findIndex((item) => item.viewId && item.viewId === searchParams.get('viewId'))
        if (viewIndex >= 0) return viewIndex
        const active = subitems.find((item) => !item.viewId && (item.pathnames?.includes(pathname) || item.href?.split('?')[0] === pathname))
        if (active) return subitems.indexOf(active)
        return subitems.findIndex((item) => item.viewId && item.pathnames?.some((path) => pathname === path || pathname.startsWith(`${path}/`)))
    }

    // Auto-open/close drilldown based on current page
    useEffect(() => {
        const match = allItems.find(
            (item) => item.subitems && isCurrentItemActive(item)
        )
        // Only update if the active parent changed
        if (match?.label !== drilldownItem?.label) {
            setDrilldownItem(match ?? null)
        }
    }, [pathname])

    const renderItems = (items) =>
        items.map((item) => (
            <NavItem
                key={item.label}
                icon={item.icon}
                label={item.label}
                badge={item.badge}
                warning={item.warning}
                href={item.subitems?.[0]?.href ?? item.href}
                hasSubitems={!!item.subitems}
                isActive={isCurrentItemActive(item)}
                isMobile={!!onNavigate}
                loading={openingLabel === item.label}
                disabled={Boolean(openingLabel)}
                onClick={() => handleDrilldown(item)}
                onNavigate={handleNavigate}
            />
        ))

    const isCurrentItemActive = (item) => {
        if (!item) return false
        const allPaths = flattenDeep([
            item.href,
            item.pathnames,
            item.subitems?.map((s) => s.href),
            item.subitems?.map((s) => s.pathnames),
        ]).filter(Boolean)
        return allPaths.some(
            (p) => pathname === p || pathname.startsWith(p + '/')
        )
    }

    const nudge = 10

    const variants = {
        enter: (d) => ({ opacity: 0, x: d * nudge }),
        center: { opacity: 1, x: 0 },
        exit: (d) => ({ opacity: 0, x: d * -nudge }),
    }

    const searchInput = (
        <div className="px-3 pt-3 pb-1 flex gap-1.5">
            <div
                onClick={() => bus.emit('openGlobalSearch')}
                className="flex-1 flex items-center gap-2.5 px-3 py-2 rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer select-none"
            >
                <SearchLg
                    width={16}
                    height={16}
                    className="shrink-0 text-gray-400"
                />
                <span className="text-sm text-gray-400 flex-1">
                    Rechercher...
                </span>
                <kbd className="hidden sm:inline text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-sm font-mono">
                    ⌘K
                </kbd>
            </div>
            <Tooltip label="Rechercher par numéro">
                <div
                    onClick={() => bus.emit('openNumberLookup')}
                    className="flex items-center justify-center w-[38px] h-[38px] shrink-0 rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer select-none"
                >
                    <Hash02 width={16} height={16} className="text-gray-500" />
                </div>
            </Tooltip>
            <Tooltip label="Scanner">
                <div
                    onClick={() => bus.emit('openCameraScanner')}
                    className="flex items-center justify-center w-[38px] h-[38px] shrink-0 rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer select-none"
                >
                    <Scan width={16} height={16} className="text-gray-500" />
                </div>
            </Tooltip>
        </div>
    )

    return (
        <div className="flex flex-col overflow-hidden h-full">
            {!onNavigate && searchInput}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <AnimatePresence
                    mode="popLayout"
                    initial={false}
                    custom={direction}
                >
                    {!currentDrilldown ? (
                        <motion.div
                            key="main"
                            custom={direction}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                                duration: 0.2,
                                ease: [0.4, 0, 0.2, 1],
                            }}
                            className="px-2 py-2 flex flex-col gap-0.5"
                        >
                            {renderItems(allItems)}
                        </motion.div>
                    ) : (
                        <motion.div
                            key="drilldown"
                            custom={direction}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                                duration: 0.2,
                                ease: [0.4, 0, 0.2, 1],
                            }}
                            className="px-2 pt-2 pb-2"
                        >
                            <button
                                onClick={handleBack}
                                className="flex items-center gap-1.5 w-full px-4 py-2.5 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                            >
                                <ChevronLeft
                                    width={18}
                                    height={18}
                                    className="shrink-0"
                                />
                                <span className="font-medium text-sm text-gray-900">
                                    {currentDrilldown.label}
                                </span>

                                {(currentDrilldown.badge > 0 ||
                                    currentDrilldown.warning > 0) && (
                                    <div className="flex items-center gap-1.5 ml-auto">
                                        {currentDrilldown.badge > 0 && (
                                            <div className="bg-baltic text-white rounded-full text-[11px] font-medium min-w-[20px] text-center px-1.5 py-0.5">
                                                {currentDrilldown.badge > 999
                                                    ? '999+'
                                                    : currentDrilldown.badge}
                                            </div>
                                        )}
                                        {currentDrilldown.warning > 0 && (
                                            <div className="bg-red-500 text-white rounded-full text-[11px] font-medium min-w-[20px] text-center px-1.5 py-0.5">
                                                {currentDrilldown.warning > 999
                                                    ? '999+'
                                                    : currentDrilldown.warning}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </button>

                            <div className="flex flex-col gap-0.5 px-2 pt-2">
                                {currentDrilldown.subitems?.map(
                                    (subitem, index) => {
                                        const activeIndex =
                                            getActiveSubitemIndex(
                                                currentDrilldown.subitems
                                            )
                                        const isActive = optimisticHref
                                            ? subitem.href === optimisticHref
                                            : index === activeIndex &&
                                              isCurrentItemActive(currentDrilldown)

                                        return (
                                            <Link
                                                key={index}
                                                to={subitem.href ?? ''}
                                                onClick={() =>
                                                    handleNavigate(subitem.href)
                                                }
                                                className={classNames(
                                                    'flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors duration-150 hover:bg-gray-100 active:bg-gray-200 text-sm',
                                                    {
                                                        'bg-gray-100 hover:bg-gray-200 text-baltic font-medium':
                                                            isActive,
                                                        'text-gray-600':
                                                            !isActive,
                                                    }
                                                )}
                                            >
                                                <span className="min-w-0 truncate" title={subitem.label}>{subitem.label}</span>

                                                {subitem.warning > 0 && (
                                                    <div className="ml-auto text-red-500">
                                                        <span className="text-xs tabular-nums">{subitem.warning}</span>
                                                    </div>
                                                )}
                                            </Link>
                                        )
                                    }
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    )
}
